import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  Tab,
  TabProps,
  Tabs,
  Typography,
} from "@mui/material";
import * as React from "react";
import LogoutIcon from "@mui/icons-material/Logout";
import { useLanguage } from "./Language";
import { AccountSummary } from "@tlc/ls2pac/dto/AccountSummary";
import SavedSearchIcon from "@mui/icons-material/SavedSearch";
import ListIcon from "@mui/icons-material/List";
import HistoryIcon from "@mui/icons-material/History";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { UserAvatar } from "./UserAvatar";
import { Link, useLocation } from "react-router-dom";
import { useSupportedFunctionality } from "./SupportedFunctionality";
import { useLocationConfiguration } from "./LocationConfiguration";
import { isLinkedAccountsEnabled } from "./pages/account/LinkedAccountsPage";
import { useGlobalConfig } from "./GlobalConfig";
import { AccountHolder } from "./Account";
import { Patron } from "@tlc/ls2pac/dto/Patron";

export interface RolePermissions {
  allowPublishToBookriver: boolean;
  allowPublishToDisplayCase: boolean;
  allowPublishToSmartGraphic: boolean;
}

export interface RoleView {
  id: number;
  name: string;
  description: string;
  permissions: RolePermissions;
  authority: string;
}

export interface Address {
  address: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
}

export interface UserPaidFineView {}

export interface ManagedConfigurationId {}

export interface UserView {
  id: number;
  guid: string;
  patronId: string;
  pin: string;
  username: string;
  password: string;
  confirmPassword: string;
  passwordHint: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  website: string;
  address: Address;
  version: number;
  roles: RoleView[];
  enabled: boolean;
  accountExpired: boolean;
  accountLocked: boolean;
  credentialsExpired: boolean;
  firstTime: boolean;
  usernameSet: boolean;
  local: boolean;
  issuingBranch: number;
  defaultRequestPickupBranch: number;
  topRole: RoleView;
  userPaidFines: UserPaidFineView[];
  authenticationKey: string;
  managedConfigurationIds: ManagedConfigurationId[];
  googleId: string;
  googleEmail: string;
  keepAliveTime: number;
  fullName: string;
}

export interface AccountMenuProps {
  accountSummary: AccountSummary;
  patron: Patron | null;
  user: UserView;
  sx?: SxProps;
}

export const accountSummaryPath = "/myaccount/summary";
export const accountLinkedAccountsPath = "/myaccount/linked";
export const accountLoansPath = "/myaccount/loans";
export const accountHoldsPath = "/myaccount/holds";
export const accountFinesPath = "/myaccount/fines";
export const accountHistoryPath = "/myaccount/history";
export const accountListsPath = "/myaccount/lists";
export const accountSearchesPath = "/myaccount/searches";
export const configTagsPath = "/config/tags";
export const configLandingPagePath = "/config/landing-page";
export const configBrandingPath = "/config/branding";
export const logoutPath = "/logout";

export const convertPenniesToCurrency = (value: number, locale = "en-US") => {
  const pennies = !value || isNaN(value) ? 0 : value;
  const dollars = pennies / 100;
  const formattedCurrency = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(dollars);

  return formattedCurrency;
};

export const useMenuTabLabel = ({
  label,
  count,
  active,
  isErrorChip,
  isErrorBadge,
}: MenuLinkProps | MenuExternalLinkProps) => {
  return (
    <>
      <Typography sx={{ flexGrow: 1, textAlign: "left" }} noWrap>
        {label}
      </Typography>
      {!!count && isErrorBadge && (
        <Badge color="error" variant="dot">
          <Chip
            size="small"
            variant={active ? "filled" : "outlined"}
            color={active ? "secondary" : "default"}
            label={count}
          />
        </Badge>
      )}
      {!!count && !isErrorBadge && (
        <Chip
          size="small"
          variant={active ? "filled" : "outlined"}
          color={active ? "secondary" : isErrorChip ? "error" : "default"}
          label={count}
        />
      )}
    </>
  );
};

export const useMenuTabStyles = ({
  divider,
}: MenuLinkProps | MenuExternalLinkProps) => {
  const borderTop =
    divider === true
      ? { borderTop: "thin solid var(--mui-palette-divider)" }
      : {};
  return {
    minHeight: "48px",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    pl: 0,
    ...borderTop,
  };
};

interface CommonProps {
  count?: number | string;
  active?: boolean;
  isErrorChip?: boolean;
  isErrorBadge?: boolean;
  divider?: boolean;
}

interface MenuLinkProps
  extends CommonProps,
    Omit<TabProps<typeof Link>, "to"> {}
interface MenuExternalLinkProps
  extends CommonProps,
    Omit<TabProps<"a">, "href"> {}

export const MenuLink = ({
  value,
  icon,
  label,
  count,
  active,
  isErrorChip,
  isErrorBadge,
  divider,
  ...props
}: MenuLinkProps) => {
  console.log("MenuLink props", props);
  return (
    <Tab
      component={Link}
      to={value}
      sx={useMenuTabStyles({ divider })}
      icon={<Box sx={{ minWidth: "56px" }}>{icon}</Box>}
      iconPosition="start"
      label={useMenuTabLabel({
        label,
        count,
        active,
        isErrorChip,
        isErrorBadge,
      })}
      {...props}
    ></Tab>
  );
};
export const MenuExternalLink = ({
  value,
  icon,
  label,
  count,
  active,
  isErrorChip,
  isErrorBadge,
  divider,
  ...props
}: MenuExternalLinkProps) => {
  return (
    <Tab
      component={"a"}
      href={value}
      rel="noopener noreferrer"
      sx={useMenuTabStyles({ divider })}
      icon={<Box sx={{ minWidth: "56px" }}>{icon}</Box>}
      iconPosition="start"
      label={useMenuTabLabel({
        label,
        count,
        active,
        isErrorChip,
        isErrorBadge,
      })}
      {...props}
    ></Tab>
  );
};

export const AccountMenu: React.FC<AccountMenuProps> = ({
  accountSummary,
  patron,
  user,
  sx,
}) => {
  const { translate } = useLanguage();
  const location = useLocation();
  const { supportedFunctionality } = useSupportedFunctionality();

  // TODO prefetch this in the root loader
  const { locationConfiguration } = useLocationConfiguration();
  const globalConfig = useGlobalConfig();
  const showLinkedAccountsTab = isLinkedAccountsEnabled(
    user,
    patron,
    locationConfiguration,
    globalConfig
  );

  const currentPath = location.pathname;
  const tabPaths = [
    accountSummaryPath,
    accountLinkedAccountsPath,
    accountLoansPath,
    accountHoldsPath,
    accountFinesPath,
    accountHistoryPath,
    accountListsPath,
    accountSearchesPath,
    configTagsPath,
    configLandingPagePath,
    configBrandingPath,
  ];
  const pathMatch = tabPaths.find((path) => currentPath.startsWith(path));
  const currentTab = pathMatch ? pathMatch : false;

  const isConfigAdmin = AccountHolder.isConfigAdmin;
  const notificationCount: number =
    (accountSummary.arrivedHolds > 0 ? 1 : 0) +
    (accountSummary.fees > 0 ? 1 : 0) +
    (accountSummary.itemsOverdue > 0 ? 1 : 0) +
    (accountSummary.status !== "Active" ? 1 : 0);

  return (
    <>
      <Box sx={{ ...sx }}>
        <List>
          <ListItem sx={{ pb: 0 }}>
            <ListItemIcon>
              {patron && (
                <Badge
                  color={
                    accountSummary.status === "Active" ? "success" : "error"
                  }
                  overlap="circular"
                  badgeContent=" "
                  variant="dot"
                >
                  <UserAvatar user={user}></UserAvatar>
                </Badge>
              )}
              {!patron && <AdminPanelSettingsIcon />}
            </ListItemIcon>
            <ListItemText
              primary={`${user.firstName}${translate("possession")} ${translate(
                "account"
              )}`}
              secondary={accountSummary.status}
            ></ListItemText>
          </ListItem>
          <ListItem sx={{ pt: 0 }}>
            <ListItemIcon />
            <Button
              component={Link}
              to={logoutPath}
              color="primary"
              variant="outlined"
            >
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />{" "}
              {translate("logout")}
            </Button>
          </ListItem>
        </List>
        <Divider />
        <Tabs
          variant={"scrollable"}
          value={currentTab}
          orientation="vertical"
          textColor="secondary"
          indicatorColor="secondary"
          allowScrollButtonsMobile={true}
        >
          {patron && (
            <MenuLink
              active={currentTab === accountSummaryPath}
              value={accountSummaryPath}
              label={translate("summary")}
              isErrorChip={notificationCount > 0}
              count={notificationCount > 0 ? notificationCount : null}
            ></MenuLink>
          )}
          {showLinkedAccountsTab && (
            <MenuLink
              active={currentTab === accountLinkedAccountsPath}
              value={accountLinkedAccountsPath}
              label={translate("linkedAccountsTab")}
            />
          )}
          {patron && (
            <MenuLink
              active={currentTab === accountLoansPath}
              value={accountLoansPath}
              label={translate("loans")}
              count={
                accountSummary.loanCount > 0 ? accountSummary.loanCount : null
              }
              isErrorBadge={accountSummary.itemsOverdue > 0}
            />
          )}
          {patron && (
            <MenuLink
              active={currentTab === accountHoldsPath}
              value={accountHoldsPath}
              label={translate("requests")}
              count={
                accountSummary.pendingHolds > 0 ||
                accountSummary.arrivedHolds > 0
                  ? accountSummary.pendingHolds + accountSummary.arrivedHolds
                  : null
              }
              isErrorBadge={accountSummary.arrivedHolds > 0}
            />
          )}
          {patron && (
            <MenuLink
              active={currentTab === accountFinesPath}
              value={accountFinesPath}
              label={translate("fines")}
              count={
                accountSummary.fees > 0
                  ? convertPenniesToCurrency(accountSummary.fees)
                  : null
              }
              isErrorChip={true}
            />
          )}
          {patron && supportedFunctionality?.supportsLoanHistory && (
            <MenuLink
              active={currentTab === accountHistoryPath}
              value={accountHistoryPath}
              icon={<HistoryIcon />}
              label={translate("loanHistory")}
            />
          )}
          <MenuLink
            active={currentTab === accountListsPath}
            value={accountListsPath}
            icon={<ListIcon />}
            label={translate("savedLists")}
            count={
              accountSummary.listCount > 0 ? accountSummary.listCount : null
            }
          />
          <MenuLink
            active={currentTab === accountSearchesPath}
            value={accountSearchesPath}
            icon={<SavedSearchIcon />}
            label={translate("savedSearches")}
            count={
              accountSummary.savedSearchCount > 0
                ? accountSummary.savedSearchCount
                : null
            }
          />

          {isConfigAdmin && (
            <MenuLink
              divider={true}
              active={currentTab === configBrandingPath}
              value={configBrandingPath}
              label={translate("branding")}
            />
          )}
          {isConfigAdmin && (
            <MenuLink
              active={currentTab === configLandingPagePath}
              value={configLandingPagePath}
              label={translate("landingPageConfiguration")}
            />
          )}
          {isConfigAdmin && (
            <MenuLink
              active={currentTab === configTagsPath}
              value={configTagsPath}
              label={translate("tagManagement")}
            />
          )}
        </Tabs>
      </Box>
    </>
  );
};
